import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, model } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BsModalService } from 'ngx-bootstrap/modal';
import { take } from 'rxjs/operators';
import { DurationManualInputModalComponent } from './duration-manual-input-modal/duration-manual-input-modal.component';
import { SignalFieldBaseDirective } from '../signal-field-base.directive';
import { formatDurationMinutes } from '../../utils/format.utils';

/** what the box shows while the field is empty */
const emptyDisplayValue = '';

/**
 * Low-level duration stepper for signal forms.
 *
 * **The value is a total number of minutes, not a `moment.Duration`.** That is the
 * one substantive decision in porting this widget, and it buys three things: the
 * slice stays trivially serializable (a `Duration` is a class instance, which the
 * root store's `strictStateSerializability` check rejects), the stepper arithmetic is
 * plain addition, and `min`/`max` arrive through the `FormUiControl` contract from the
 * schema's `min()`/`max()` rules — exactly as they do for `NumberStepperFieldComponent`,
 * which an ISO 8601 duration string could not offer. It also matches the wire format
 * the legacy model already supports as `DURATION_ODATA_FORMAT.MINUTES`. Callers that
 * need a `Duration` build one at the edge with `moment.duration(minutes, 'minutes')`.
 *
 * ```html
 * <pace-core-duration-stepper-field [formField]="signalForm.travelTime" [step]="5" />
 * ```
 *
 * The text box is deliberately not typeable, as in the reactive-forms widget: clicking
 * it opens {@link DurationManualInputModalComponent}, which speaks minutes as well — so
 * no `DurationModel` is built anywhere on this path. A value coming back from the dialog
 * is snapped to the nearest step, matching what `DurationModel.set()` does in the legacy
 * path.
 */
@Component({
    selector: 'pace-duration-stepper-field',
    templateUrl: './duration-stepper-field.component.html',
    styleUrls: ['./duration-stepper-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DurationStepperFieldComponent extends SignalFieldBaseDirective<number | null> {
    /** total minutes; `null` while the field is empty */
    public readonly value = model<number | null>(null);

    // --- `FormUiControl` inputs, all bound automatically by `[formField]` -----

    /** bounds in minutes */
    public readonly min = input<number | undefined>(undefined);
    public readonly max = input<number | undefined>(undefined);

    // --- own inputs ----------------------------------------------------------

    /** amount in minutes a button press adds or subtracts */
    public readonly step = input(1);

    /** the duration as `HH:mm`, counting past 24h rather than wrapping */
    public readonly displayValue = computed(() => {
        const minutes = this.value();

        return minutes === null ? emptyDisplayValue : formatDurationMinutes(minutes);
    });

    /** the value a button press would produce, from 0 when the field is empty */
    private readonly incremented = computed(() => (this.value() ?? 0) + this.step());
    private readonly decremented = computed(() => (this.value() ?? 0) - this.step());

    /**
     * A step that would cross a bound is refused rather than clamped to it, which is
     * how the reactive-forms stepper behaves.
     */
    public readonly canIncrement = computed(() => this.editable() && this.incremented() <= (this.max() ?? Infinity));
    public readonly canDecrement = computed(() => this.editable() && this.decremented() >= (this.min() ?? -Infinity));

    private readonly modalService = inject(BsModalService);
    private readonly destroyRef = inject(DestroyRef);

    public increment(): void {
        if (this.canIncrement()) {
            this.setValue(this.incremented());
        }
    }

    public decrement(): void {
        if (this.canDecrement()) {
            this.setValue(this.decremented());
        }
    }

    /**
     * Opens the hours/minutes dialog. Both sides deal in total minutes, so the state
     * handed over is the value and the bounds as they already are.
     */
    public openManualInput(): void {
        if (!this.editable()) {
            return;
        }

        const modalInstance = this.modalService.show(DurationManualInputModalComponent, {
            backdrop: 'static',
            class: 'modal-sm',
            initialState: {
                durationInMinutes: this.value() ?? undefined,
                minMinutes: this.min() ?? 0,
                maxMinutes: this.max(),
            },
            ariaLabelledBy: 'duration-manual-input-modal-title',
        });

        modalInstance.content?.durationChanged
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((minutes: number) => this.setValue(this.roundToStep(minutes)));
    }

    /**
     * @param minutes the duration to snap
     * @returns the nearest multiple of the configured step
     */
    private roundToStep(minutes: number): number {
        const step = this.step();

        return step > 0 ? Math.round(minutes / step) * step : minutes;
    }

    /**
     * A button press or a modal save is an interaction, even though it produces no blur.
     *
     * @param next the new duration in minutes
     */
    private setValue(next: number): void {
        this.value.set(next);
        this.touched.set(true);
    }
}
